import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Translate } from 'react-localize-redux';
import { IconSizes } from '../../components/foundations/Icon';
import SubPageHeader from '../../components/molecules/SubPageHeader';
import Tooltip from '../../components/molecules/Tooltip';
import { ConfirmChangesDialog, useNavigationBlocker } from '../../components/organisms/NavigationBlocker';
import { Button, Tabs } from '../../components/widgets';
import AppSitePageStep from '../containers/AppSitePageStep';
import PartnerPageStep from '../containers/PartnerPageStep';
import ScreenPageStep from '../containers/ScreenPageStep';
import SectionsPageStep from '../containers/SectionsPageStep';
import { SECTION_PAGES_STEPS, SECTIONS_PAGES_CONFIG } from '../sections';
import {
    MANAGE_PERMISSIONS_STEPS,
    RECAPTCHA_PUBLIC_KEY,
    SECTIONS_STEPS,
    translationPrefix as translationRootPrefix,
    WIZARD_MODES,
} from '../utils';
import css from './ManagePermissionsPage.css';

const translationPrefix = `${translationRootPrefix}.MANAGE_PERMISSIONS_PAGE`;

const SECTION_PAGES_STEPS_DATA = SECTION_PAGES_STEPS.reduce(
    (acc, step) => ({
        ...acc,
        [step]: SectionsPageStep,
    }),
    {}
);

const STEPS_DATA = {
    [MANAGE_PERMISSIONS_STEPS.apps]: AppSitePageStep,
    [MANAGE_PERMISSIONS_STEPS.partners]: PartnerPageStep,
    ...SECTION_PAGES_STEPS_DATA,
    [MANAGE_PERMISSIONS_STEPS.screens]: ScreenPageStep,
};

const ManagePermissionsPage = ({
    onHidePage,
    onDirectHidePage,
    hasChanges,
    confirmChangesDialogOpen,
    onConfirmChangesAccepted,
    onConfirmChangesRejected,
    agencySelectionForm,
    mode,
    activeMemberType,
    onManagePermissionsFinished,
    onSelectedTabIndexChanged,
    page: { loading, selectedTabIndex, stepValidationStatus },
}) => {
    const translationMemberPrefix = `${translationPrefix}.${activeMemberType}`;
    const recaptchaRef = useRef(null);

    useNavigationBlocker(hasChanges, onDirectHidePage);

    const tabs = Object.values(MANAGE_PERMISSIONS_STEPS).map((step, index) => ({
        id: step,
        label: `${translationPrefix}.TABS.${step.toUpperCase()}`,
        disabled: (mode === WIZARD_MODES.create && index > selectedTabIndex + 1) || !stepValidationStatus.isValid,
    }));
    const isFirstTab = selectedTabIndex === 0;
    const isLastTab = selectedTabIndex === tabs.length - 1;

    const renderStep = () => {
        const selectedStep = tabs[selectedTabIndex].id;
        const StepComponent = STEPS_DATA[selectedStep];
        if (!StepComponent) return null;

        let props = {};

        if (SECTIONS_STEPS.includes(selectedStep)) {
            props = SECTIONS_PAGES_CONFIG.find(page => page.step === selectedStep);
        }

        return <StepComponent {...props} />;
    };

    const onNextClick = async () => {
        if (isLastTab) {
            const token = await recaptchaRef.current.executeAsync();
            onManagePermissionsFinished(token);
        } else {
            onSelectedTabIndexChanged(selectedTabIndex + 1);
        }
    };

    const onBackClick = () => {
        if (isFirstTab) {
            onHidePage();
        } else {
            onSelectedTabIndexChanged(selectedTabIndex - 1);
        }
    };

    return (
        <div className={css.page}>
            <SubPageHeader
                onGoBack={onHidePage}
                goBackTranslationKey={`${translationMemberPrefix}.BACK`}
                labelTranslationKey={`${translationMemberPrefix}.${mode.toUpperCase()}`}
                className={css.header}
                labelTextReplace={{
                    name: agencySelectionForm.name,
                }}
            />
            <div className={css.mainContainer}>
                {tabs.length > 1 && (
                    <Tabs
                        tabs={tabs}
                        selected={tabs[selectedTabIndex].id}
                        onClick={tabId => onSelectedTabIndexChanged(tabs.findIndex(tab => tab.id === tabId))}
                        containerClass={css.tabsContainer}
                        dividerClass={css.tabsDivider}
                        numbered
                        progress
                    />
                )}
                <div className={css.content}>{renderStep()} </div>
                <div className={css.actionsButtonsContainer}>
                    <Button
                        type={isFirstTab ? 'flat' : 'secondary'}
                        onClick={onBackClick}
                        {...(!isFirstTab && {
                            Icon: 'chevron-left',
                            iconLocation: 'left',
                            iconSize: IconSizes.X_SMALL,
                        })}
                        disabled={
                            loading || (!isFirstTab && mode === WIZARD_MODES.edit && !stepValidationStatus.isValid)
                        }
                    >
                        <Translate id={`STATIC.BUTTONS.${isFirstTab ? 'CANCEL' : 'BACK'}`} />
                    </Button>
                    <Tooltip title={stepValidationStatus.title} position="top">
                        <Button
                            type="primary"
                            onClick={onNextClick}
                            {...(!isLastTab && {
                                Icon: 'chevron-right',
                                iconLocation: 'right',
                                iconSize: IconSizes.X_SMALL,
                            })}
                            disabled={loading || !stepValidationStatus.isValid}
                            showSpinner={loading}
                            contentClass={classNames({ [css.disabledButton]: !stepValidationStatus.isValid })}
                        >
                            <Translate id={`STATIC.BUTTONS.${isLastTab ? 'SAVE_PERMISSIONS' : 'NEXT'}`} />
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_PUBLIC_KEY} />
            <ConfirmChangesDialog
                open={confirmChangesDialogOpen}
                onAccept={onConfirmChangesAccepted}
                onReject={onConfirmChangesRejected}
            />
        </div>
    );
};

ManagePermissionsPage.propTypes = {
    onHidePage: PropTypes.func.isRequired,
    onDirectHidePage: PropTypes.func.isRequired,
    hasChanges: PropTypes.bool.isRequired,
    confirmChangesDialogOpen: PropTypes.bool.isRequired,
    onConfirmChangesAccepted: PropTypes.func.isRequired,
    onConfirmChangesRejected: PropTypes.func.isRequired,
    agencySelectionForm: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
    }).isRequired,
    mode: PropTypes.string.isRequired,
    activeMemberType: PropTypes.string.isRequired,
    onManagePermissionsFinished: PropTypes.func.isRequired,
    onSelectedTabIndexChanged: PropTypes.func.isRequired,
    page: PropTypes.shape({
        show: PropTypes.bool,
        loading: PropTypes.bool,
        selectedTabIndex: PropTypes.number,
        stepValidationStatus: PropTypes.shape({
            isValid: PropTypes.bool,
            title: PropTypes.string,
        }),
    }).isRequired,
};

export default ManagePermissionsPage;
